/**
 * Auth context provider. Wraps the auth.ts module in a React context so
 * components can read the current user + react to auth state changes.
 *
 * The context object, value type, and useAuth hook live in ./auth-context so
 * this file only exports the AuthProvider component (react-refresh).
 */
import { useEffect, useState, type ReactNode } from "react";
import {
  getCurrentUser, signIn as authSignIn,
  completeNewPassword as authCompletePassword,
  forgotPassword as authForgotPassword,
  confirmForgotPassword as authConfirmForgotPassword,
  signOut as authSignOut,
  type AuthenticatedUser,
} from "../lib/auth";
import type { CognitoUser } from "amazon-cognito-identity-js";
import { AuthContext } from "./auth-context";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = async () => {
    const u = await getCurrentUser();
    setUser(u);
  };

  useEffect(() => {
    // One-time session probe on mount. setState runs only AFTER the async
    // getCurrentUser resolves (and behind an `active` guard against unmount),
    // so there's no synchronous setState-in-effect.
    let active = true;
    (async () => {
      const u = await getCurrentUser();
      if (active) {
        setUser(u);
        setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const signIn = async (username: string, password: string) => {
    const result = await authSignIn(username, password);
    if (result.kind === "success") setUser(result.user);
    return result;
  };

  const completeNewPassword = async (
    cognitoUser: CognitoUser, newPassword: string,
  ) => {
    const result = await authCompletePassword(cognitoUser, newPassword);
    if (result.kind === "success") setUser(result.user);
    return result;
  };

  // Neither forgotPassword nor confirmForgotPassword authenticates the
  // user (Cognito's ConfirmForgotPassword doesn't return a session — the
  // user still signs in normally afterward with the new password), so
  // these are thin passthroughs with no user-state update, unlike signIn/
  // completeNewPassword above.
  const forgotPassword = (username: string) => authForgotPassword(username);

  const confirmForgotPassword = (
    username: string, code: string, newPassword: string,
  ) => authConfirmForgotPassword(username, code, newPassword);

  const signOut = () => {
    authSignOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, isLoading, signIn, completeNewPassword,
      forgotPassword, confirmForgotPassword, signOut, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
